import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { User, Home, Ruler, Send, Loader2, FileText, Download, Edit } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';
import { MATERIALS } from './constants';
import AmendSurveyDialog from './AmendSurveyDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const ViewSurveyDialog = ({ survey, open, onOpenChange, getWindowTypeLabel, onSurveyUpdated }) => {
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [showAmendDialog, setShowAmendDialog] = useState(false);
  const [sendingQuote, setSendingQuote] = useState(false);
  const [quoteData, setQuoteData] = useState({
    material: 'Paulownia',
    louvreSize: '89mm',
    colour: 'Pure White',
    hingeColour: 'White',
    windows: [],
    internalNotes: '',
    customerNotes: ''
  });

  // Initialize window prices when showing quote form
  const initializeQuoteForm = () => {
    if (survey.openings && survey.openings.length > 0) {
      const windows = survey.openings.map((opening, index) => {
        // Get material from opening or default to paulownia
        const openingMaterial = MATERIALS.find(m => m.id === opening.material) || 
                                MATERIALS.find(m => m.id === 'paulownia');
        return {
          label: opening.room_location || `Window ${index + 1}`,
          layoutCode: opening.layout || '',
          material: openingMaterial?.id || 'paulownia',
          materialName: openingMaterial?.name || 'Paulownia Timber',
          pricePerSqm: openingMaterial?.pricePerSqm || 300,
          width: opening.width || 0,
          height: opening.height || 0,
          price: ''
        };
      });
      setQuoteData(prev => ({ ...prev, windows, internalNotes: '', customerNotes: '' }));
    }
    setShowQuoteForm(true);
  };

  const updateWindowLayoutCode = (index, layoutCode) => {
    const updatedWindows = [...quoteData.windows];
    updatedWindows[index].layoutCode = layoutCode;
    setQuoteData(prev => ({ ...prev, windows: updatedWindows }));
  };

  const updateWindowMaterial = (index, materialId) => {
    const material = MATERIALS.find(m => m.id === materialId);
    const updatedWindows = [...quoteData.windows];
    updatedWindows[index].material = materialId;
    updatedWindows[index].materialName = material?.name || 'Paulownia Timber';
    updatedWindows[index].pricePerSqm = material?.pricePerSqm || 300;
    setQuoteData(prev => ({ ...prev, windows: updatedWindows }));
  };

  const updateWindowPrice = (index, price) => {
    const updatedWindows = [...quoteData.windows];
    updatedWindows[index].price = price;
    setQuoteData(prev => ({ ...prev, windows: updatedWindows }));
  };

  const calculateTotal = () => {
    return quoteData.windows.reduce((sum, w) => sum + (parseFloat(w.price) || 0), 0);
  };

  // Calculate suggested prices based on material and dimensions
  const calculateSuggestedPrices = () => {
    return quoteData.windows.map(w => {
      const sqm = (w.width * w.height) / 1000000;
      return Math.round(sqm * w.pricePerSqm * 100) / 100;
    });
  };

  // Apply a custom total and distribute proportionally across windows
  const applyCustomTotal = (newTotal) => {
    const suggestedPrices = calculateSuggestedPrices();
    const suggestedTotal = suggestedPrices.reduce((sum, p) => sum + p, 0);
    
    if (suggestedTotal === 0) return;
    
    const ratio = newTotal / suggestedTotal;
    const updatedWindows = quoteData.windows.map((w, idx) => ({
      ...w,
      price: (Math.round(suggestedPrices[idx] * ratio * 100) / 100).toString()
    }));
    
    setQuoteData(prev => ({ ...prev, windows: updatedWindows }));
  };

  // Auto-fill prices based on calculated values
  const autoFillPrices = () => {
    const suggestedPrices = calculateSuggestedPrices();
    const updatedWindows = quoteData.windows.map((w, idx) => ({
      ...w,
      price: suggestedPrices[idx].toString()
    }));
    setQuoteData(prev => ({ ...prev, windows: updatedWindows }));
  };

  const handleSendQuote = async () => {
    // Validate all prices are entered
    const hasEmptyPrices = quoteData.windows.some(w => !w.price || parseFloat(w.price) <= 0);
    if (hasEmptyPrices) {
      toast({
        title: "Missing Prices",
        description: "Please enter a price for all windows",
        variant: "destructive"
      });
      return;
    }

    // Get summary of materials used
    const materialsUsed = [...new Set(quoteData.windows.map(w => w.materialName))];

    setSendingQuote(true);
    try {
      const response = await axios.post(`${API}/api/quotes/send-formal-quote`, {
        customer_name: survey.customer_name,
        customer_email: survey.customer_email,
        customer_phone: survey.customer_phone || '',
        customer_address: survey.customer_address || `${survey.customer_postcode || ''}`,
        material: materialsUsed.join(', '), // Summary of all materials
        louvre_size: quoteData.louvreSize,
        colour: quoteData.colour,
        hinge_colour: quoteData.hingeColour,
        windows: quoteData.windows.map(w => ({
          label: w.label,
          layout_code: w.layoutCode || '-',
          price: parseFloat(w.price),
          material: w.materialName // Include per-window material
        })),
        total_price: calculateTotal(),
        customer_notes: quoteData.customerNotes || ''
      });

      if (response.data.success) {
        toast({
          title: "Quote Sent!",
          description: `Quote sent to ${survey.customer_email}`,
        });
        setShowQuoteForm(false);
        onOpenChange(false);
      } else {
        throw new Error(response.data.error || 'Failed to send quote');
      }
    } catch (error) {
      console.error('Error sending quote:', error);
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to send quote. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSendingQuote(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Survey {survey.survey_number}
            <Badge className={survey.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-[#8CB89F]/20 text-[#2F4F4F]'}>
              {survey.status?.replace('_', ' ').toUpperCase()}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {!showQuoteForm ? (
          <>
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Customer Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="font-medium">Name:</span> {survey.customer_name}</div>
                  <div><span className="font-medium">Email:</span> {survey.customer_email || 'N/A'}</div>
                  <div><span className="font-medium">Phone:</span> {survey.customer_phone || 'N/A'}</div>
                  <div><span className="font-medium">Postcode:</span> {survey.customer_postcode || 'N/A'}</div>
                  <div className="col-span-2"><span className="font-medium">Address:</span> {survey.customer_address || 'N/A'}</div>
                </div>
              </div>

              {/* Property Info */}
              <div className="bg-[#8CB89F]/10 p-4 rounded-lg">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Home className="h-4 w-4" />
                  Property Details
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="font-medium">Type:</span> {survey.property_type || 'N/A'}</div>
                  <div><span className="font-medium">Surveyor:</span> {survey.surveyor_name || 'N/A'}</div>
                  {survey.property_notes && (
                    <div className="col-span-2"><span className="font-medium">Notes:</span> {survey.property_notes}</div>
                  )}
                </div>
              </div>

              {/* Openings */}
              {survey.openings && survey.openings.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Ruler className="h-4 w-4" />
                    Openings ({survey.openings.length})
                  </h4>
                  <div className="space-y-3">
                    {survey.openings.map((opening, index) => {
                      const openingMaterial = MATERIALS.find(m => m.id === opening.material);
                      return (
                        <Card key={opening.opening_id || index} className="border">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium">{opening.room_location}</h5>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">{getWindowTypeLabel(opening.window_type || opening.layout_type)}</Badge>
                                {openingMaterial && (
                                  <Badge className="bg-[#8CB89F]/20 text-[#2F4F4F]">
                                    {openingMaterial.name}
                                  </Badge>
                                )}
                                {opening.shutter_style && (
                                  <Badge variant="secondary" className="capitalize">
                                    {opening.shutter_style?.replace('_', ' ')}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            
                            {/* Display measurements */}
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">Dimensions:</span> {opening.width || opening.flat_window_measurements?.width || opening.standard_measurements?.width}mm × {opening.height || opening.flat_window_measurements?.height || opening.standard_measurements?.height}mm
                              {opening.layout && <span className="ml-2">| <span className="font-medium">Layout:</span> {opening.layout}</span>}
                            </div>
                            
                            {/* Bay Posts */}
                            {opening.bay_posts && opening.bay_posts.length > 0 && (
                              <div className="text-sm text-gray-600 mt-1">
                                <span className="font-medium">Bay Posts:</span> {opening.bay_posts.map((bp, i) => `B${i+1}: ${bp.measurement}mm @ ${bp.angle}°`).join(', ')}
                              </div>
                            )}
                            
                            {/* T Posts */}
                            {opening.t_posts && opening.t_posts.length > 0 && (
                              <div className="text-sm text-gray-600 mt-1">
                                <span className="font-medium">T-Posts:</span> {opening.t_posts.map((tp, i) => `T${i+1}: ${tp}mm`).join(', ')}
                              </div>
                            )}

                            <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
                              {opening.louvre_size && <span>Louvre: {opening.louvre_size}</span>}
                              {opening.colour && <span>• Colour: {opening.colour}</span>}
                              {opening.mount_type && <span>• Mount: {opening.mount_type?.replace('_', ' ')}</span>}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Notes */}
              {(survey.general_notes || survey.access_notes || survey.installation_notes) && (
                <div className="space-y-3">
                  <h4 className="font-medium">Notes</h4>
                  {survey.general_notes && (
                    <div className="bg-yellow-50 p-3 rounded">
                      <span className="font-medium text-sm">General:</span>
                      <p className="text-sm">{survey.general_notes}</p>
                    </div>
                  )}
                  {survey.access_notes && (
                    <div className="bg-orange-50 p-3 rounded">
                      <span className="font-medium text-sm">Access:</span>
                      <p className="text-sm">{survey.access_notes}</p>
                    </div>
                  )}
                  {survey.installation_notes && (
                    <div className="bg-[#8CB89F]/10 p-3 rounded">
                      <span className="font-medium text-sm">Installation:</span>
                      <p className="text-sm">{survey.installation_notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  window.open(`${API}/api/surveys/${survey.id}/pdf`, '_blank');
                }}
              >
                <FileText className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowAmendDialog(true)}
                className="border-[#8CB89F] text-[#2F4F4F] hover:bg-[#8CB89F]/10"
                data-testid="amend-survey-btn"
              >
                <Edit className="h-4 w-4 mr-2" />
                Amend Survey
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
              {survey.status === 'completed' && survey.customer_email && (
                <Button 
                  onClick={initializeQuoteForm}
                  className="bg-sage-500 hover:bg-sage-600"
                  data-testid="send-quote-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Quote
                </Button>
              )}
            </DialogFooter>
          </>
        ) : (
          <>
            {/* Quote Form */}
            <div className="space-y-6">
              <div className="bg-sage-50 p-4 rounded-lg border border-sage-200">
                <h4 className="font-medium text-sage-800 mb-1">Sending Quote to:</h4>
                <p className="text-sm text-sage-700">{survey.customer_name} ({survey.customer_email})</p>
              </div>

              {/* Product Details - Shared Options */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Louvre Size</Label>
                  <Select value={quoteData.louvreSize} onValueChange={(v) => setQuoteData(prev => ({ ...prev, louvreSize: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="47mm">47mm</SelectItem>
                      <SelectItem value="63mm">63mm</SelectItem>
                      <SelectItem value="76mm">76mm</SelectItem>
                      <SelectItem value="89mm">89mm</SelectItem>
                      <SelectItem value="114mm">114mm</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Colour</Label>
                  <Input 
                    value={quoteData.colour}
                    onChange={(e) => setQuoteData(prev => ({ ...prev, colour: e.target.value }))}
                    placeholder="e.g. Pure White"
                  />
                </div>
                <div>
                  <Label>Hinge Colour</Label>
                  <Select value={quoteData.hingeColour} onValueChange={(v) => setQuoteData(prev => ({ ...prev, hingeColour: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="White">White</SelectItem>
                      <SelectItem value="Black">Black</SelectItem>
                      <SelectItem value="Chrome">Chrome</SelectItem>
                      <SelectItem value="Satin Chrome">Satin Chrome</SelectItem>
                      <SelectItem value="Antique Brass">Antique Brass</SelectItem>
                      <SelectItem value="Matching">Matching</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Window Pricing with Per-Window Material */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-medium">Window Pricing & Materials</Label>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={autoFillPrices}
                    className="text-xs"
                    data-testid="auto-fill-prices-btn"
                  >
                    Auto-fill from calculations
                  </Button>
                </div>
                <div className="space-y-3">
                  {quoteData.windows.map((window, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{window.label}</p>
                        <span className="text-xs text-gray-500">
                          {window.width}mm × {window.height}mm
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs text-gray-500 mb-1 block">Material</Label>
                          <Select 
                            value={window.material} 
                            onValueChange={(v) => updateWindowMaterial(index, v)}
                          >
                            <SelectTrigger data-testid={`window-material-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {MATERIALS.map(m => (
                                <SelectItem key={m.id} value={m.id}>
                                  {m.name} - £{m.pricePerSqm}/m²
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500 mb-1 block">Layout</Label>
                          <Input
                            value={window.layoutCode}
                            onChange={(e) => updateWindowLayoutCode(index, e.target.value.toUpperCase())}
                            className="font-mono text-sm"
                            placeholder="e.g. LBLRBR"
                            data-testid={`window-layout-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500 mb-1 block">Price</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                            <Input
                              type="number"
                              value={window.price}
                              onChange={(e) => updateWindowPrice(index, e.target.value)}
                              className="pl-7"
                              placeholder="0.00"
                              data-testid={`window-price-${index}`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes Sections */}
              <div className="space-y-4">
                {/* Internal Notes - Staff Only */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <Label className="text-sm font-medium text-amber-800 mb-2 block flex items-center gap-2">
                    <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded">STAFF ONLY</span>
                    Internal Notes
                  </Label>
                  <textarea
                    value={quoteData.internalNotes}
                    onChange={(e) => setQuoteData(prev => ({ ...prev, internalNotes: e.target.value }))}
                    className="w-full p-3 border border-amber-200 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
                    rows={3}
                    placeholder="Notes for internal use only - NOT sent to customer"
                    data-testid="internal-notes"
                  />
                </div>

                {/* Customer Notes - Visible to Customer */}
                <div className="bg-sage-50 border border-sage-200 rounded-lg p-4">
                  <Label className="text-sm font-medium text-sage-800 mb-2 block flex items-center gap-2">
                    <span className="bg-sage-500 text-white text-xs px-2 py-0.5 rounded">VISIBLE TO CUSTOMER</span>
                    Notes for Customer
                  </Label>
                  <textarea
                    value={quoteData.customerNotes}
                    onChange={(e) => setQuoteData(prev => ({ ...prev, customerNotes: e.target.value }))}
                    className="w-full p-3 border border-sage-200 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-sage-300 bg-white"
                    rows={3}
                    placeholder="These notes will be included in the customer's quote email..."
                    data-testid="customer-notes"
                  />
                </div>
              </div>

              {/* Total with Custom Price Option */}
              <div className="bg-slate-800 text-white p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-slate-300">Total Quote Price</span>
                    <p className="text-xs text-slate-400 mt-1">Edit to apply discount across all windows</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">£</span>
                    <Input
                      type="number"
                      value={calculateTotal() || ''}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value > 0) {
                          applyCustomTotal(value);
                        }
                      }}
                      className="w-32 bg-slate-700 border-slate-600 text-white text-xl font-bold text-right"
                      placeholder="0.00"
                      data-testid="total-price-input"
                    />
                  </div>
                </div>
                <div className="flex justify-between items-center mt-2 text-sm">
                  <span className="text-slate-400">Deposit (60%)</span>
                  <span className="text-sage-400 font-medium">£{(calculateTotal() * 0.6).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between items-center mt-1 text-sm">
                  <span className="text-slate-400">Balance (40%)</span>
                  <span className="text-slate-300">£{(calculateTotal() * 0.4).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>

            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setShowQuoteForm(false)}>Back</Button>
              <Button 
                onClick={handleSendQuote}
                disabled={sendingQuote}
                className="bg-sage-500 hover:bg-sage-600"
                data-testid="confirm-send-quote-btn"
              >
                {sendingQuote ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Quote to Customer
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>

      {/* Amend Survey Dialog */}
      <AmendSurveyDialog
        survey={survey}
        open={showAmendDialog}
        onOpenChange={setShowAmendDialog}
        onAmended={() => {
          setShowAmendDialog(false);
          onOpenChange(false);
          onSurveyUpdated?.();
        }}
      />
    </>
  );
};

export default ViewSurveyDialog;
